// THE `capabilities_detected` PAYLOAD — what a setup run found this repo can do.
// Split out of `../task-run-event.ts`, which is now the barrel over this directory.

import { z } from 'zod';

/**
 * One capability token on the wire. The vocabulary is deliberately OPEN
 * (`RunnerCapability` names only the ones setup emits today), so the gate is a
 * SHAPE gate, not an allowlist: lowercase-ish token characters, bounded length.
 * The tokens land in `runners.capabilities.provisions` — an untrusted box's only
 * reach into a dispatch-gate column — so the wire contract is what keeps a
 * megabyte of junk (or a token carrying markup / whitespace / a newline that
 * would never match a task's `requires` anyway) out of that jsonb.
 */
export const CapabilityTokenSchema = z
  .string()
  .trim()
  .regex(/^[a-z0-9][a-z0-9._:+-]{0,63}$/i, 'capability: [a-z0-9] then [a-z0-9._:+-] (≤64)');

/** Tokens one `capabilities_detected` event may carry (a repo needs a handful). */
export const CAPABILITIES_DETECTED_MAX_TOKENS = 32;

/**
 * The capability advert a SETUP runner streams (arc 101 doc 03): the tokens it
 * detected the repo needs and provisioned on the box
 * (`apps/runner/src/modes/setup/executor.ts`). The mothership folds them into
 * `runners.capabilities.provisions`, which the fleet assigner matches a task's
 * `requires` against (`capabilitiesSatisfy` / `filterByCapability`).
 *
 * There is no runner→mothership fleet-state transport (a box's creds grant only
 * `fleet.evt.<run_id>`), so — exactly like {@link KbLearningsPayloadSchema} — the
 * audited status event IS the write-half, and this schema is its trust boundary:
 * a box is untrusted input and the write feeds a DISPATCH gate.
 */
export const CapabilitiesDetectedPayloadSchema = z.object({
  event: z.literal('capabilities_detected'),
  /** Repo whose setup detected these needs — audit context, never the write target. */
  repo: z.string().trim().min(1).max(200),
  /** Detected + provisioned capability tokens. Empty is valid: "nothing beyond the base". */
  provisions: z.array(CapabilityTokenSchema).max(CAPABILITIES_DETECTED_MAX_TOKENS),
  /**
   * `true`: `provisions` is the box's WHOLE probed set (plan 101 row 11-8), so the
   * mothership REPLACES the box's provisions with it and a token no probe answers
   * any more retracts. Absent: a pre-11-8 box's advert (repo needs ∪ box tokens),
   * unioned as before. A literal, never `false` — absence IS the old meaning, and an
   * older mothership strips the key and unions, which is that old meaning again.
   */
  replace: z.literal(true).optional(),
  note: z.string().max(1_000).optional(),
});
export type CapabilitiesDetectedPayload = z.infer<typeof CapabilitiesDetectedPayloadSchema>;
